import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { STATIC_BASE_URL } from '../config';

const VideoIntro = ({ 
  onVideoEnd, 
  onBuildingClick, 
  coordinates = [], 
  startAtEnd = false,
  searchQuery = '',
  onSearchInputChange,
  searchResults = [],
  showSearchResults = false,
  onSearchResultClick,
  selectedSearchResult,
  onViewBuildingDetails
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [videoEnded, setVideoEnded] = useState(startAtEnd);
  const [videoReady, setVideoReady] = useState(false);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    title: ''
  });
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [, setViewportVersion] = useState(0);

  // Derslik görseli URL'ini oluştur
  const getRoomImageUrl = (roomName) => {
    // ÖNEMLİ: & karakterini önce 've' ile değiştir, sonra diğer karakterleri _ ile değiştir
    const baseFileName = roomName
      .toLowerCase()
      .replace(/&/g, 've')
      .replace(/[^a-z0-9çğıöşü]/g, '_')
      .replace(/\//g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    
    // Önce optimize edilmiş derslik görsellerini dene (.jpg uzantısı ile)
    let imageUrl = `${STATIC_BASE_URL}/static/room_images_optimized/${baseFileName}.jpg`;
    
    console.log('🆕 PC ARAMA OPTİMİZE EDİLMİŞ DERSLİK GÖRSELİ DENENİYOR (.jpg):', imageUrl, 'for room:', roomName, 'fileName:', baseFileName);
    
    return imageUrl;
  };

  // Derslik görseli tıklama
  const handleImageClick = (roomName) => {
    const imageUrl = getRoomImageUrl(roomName);
    setImageModal({
      isOpen: true,
      imageUrl: imageUrl,
      title: roomName
    });
  };

  // Modal kapatma
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      title: ''
    });
  };

  // 14 farklı renk paleti - useMemo ile optimize edildi
  const colorPalette = useMemo(() => [
    '#e74c3c', // Kırmızı
    '#3498db', // Mavi
    '#2ecc71', // Yeşil
    '#f39c12', // Turuncu
    '#9b59b6', // Mor
    '#1abc9c', // Turkuaz
    '#e67e22', // Turuncu-Kırmızı
    '#34495e', // Koyu Gri
    '#f1c40f', // Sarı
    '#e91e63', // Pembe
    '#00bcd4', // Açık Mavi
    '#4caf50', // Açık Yeşil
    '#ff9800', // Turuncu
    '#795548'  // Kahverengi
  ], []);

  // Mobildeki ham koordinatlar (800x450 sistemi)
  const testCoordinates = useMemo(() => [
    { x: 651, y: 274, building_name: "MÜHENDİSLİK FAKÜLTESİ GİRİŞ KAPISI" },
    { x: 627, y: 376, building_name: "MESLEK YÜKSEKOKULU GİRİŞ KAPISI" },
    { x: 569, y: 334, building_name: "SAĞLIK BİLİMLERİ FAKÜLTESİ GİRİŞ KAPISI" },
    { x: 308, y: 143, building_name: "DİŞ HEKİMLİĞİ FAKÜLTESİ GİRİŞ KAPISI" },
    { x: 264, y: 65, building_name: "KÜTÜPHANE GİRİŞ KAPISI" },
    { x: 218, y: 121, building_name: "REKTÖRLÜK GİRİŞ KAPISI" },
    { x: 180, y: 182, building_name: "KAFETERYA GİRİŞ KAPISI" },
    { x: 378, y: 221, building_name: "SPOR SALONU GİRİŞ KAPISI" },
    { x: 421, y: 152, building_name: "ETKİNLİK ALANI GİRİŞ KAPISI" },
    { x: 496, y: 287, building_name: "FANTOM VE PRAKİLİNİK LABORATUVARI GİRİŞ KAPISI" }
  ], []);

  useEffect(() => {
    // 3 saniye sonra skip butonunu göster
    const timer = setTimeout(() => {
      setShowSkipButton(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // startAtEnd true ise video'nun son karesine atla
  useEffect(() => {
    if (startAtEnd && videoRef.current && videoRef.current.duration) {
      videoRef.current.currentTime = videoRef.current.duration - 0.1;
      setVideoEnded(true);
      setIsPlaying(false);
    }
  }, [startAtEnd, videoRef.current?.duration]);

  const handleVideoLoad = useCallback(() => {
  }, []);

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
    setVideoEnded(true);
    
    // Video'yu son karesinde durdur
    if (videoRef.current) {
      videoRef.current.pause();
      // Son kareye git
      videoRef.current.currentTime = videoRef.current.duration - 0.1;
    }
  }, []);

  const handleSkipVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    onVideoEnd();
  }, [onVideoEnd]);

  const handleVideoClick = useCallback(() => {
    // Video bittikten sonra tıklamayı engelle
    if (videoEnded) {
      return;
    }
    
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  }, [videoEnded, isPlaying]);

  const BASE_WIDTH = 800;
  const BASE_HEIGHT = 450;
  const toClientPosition = (x, y) => {
    const container = containerRef.current;
    if (!container) return { left: 0, top: 0 };
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const scale = Math.max(width / BASE_WIDTH, height / BASE_HEIGHT);
    const displayedWidth = BASE_WIDTH * scale;
    const displayedHeight = BASE_HEIGHT * scale;
    const offsetX = (width - displayedWidth) / 2;
    const offsetY = (height - displayedHeight) / 2;
    return { left: offsetX + x * scale, top: offsetY + y * scale };
  };

  useEffect(() => {
    const onResize = () => {
      setViewportVersion(v => v + 1);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="video-intro">
      <div className={`video-container ${videoEnded ? 'video-ended' : ''}`} ref={containerRef}>
        {/* Video - her zaman göster, bittikten sonra son karesinde durur */}
        <video
          ref={videoRef}
          className={`intro-video ${videoEnded ? 'ended' : ''}`}

          onLoadedData={handleVideoLoad}
          onLoadedMetadata={() => {
            if (startAtEnd && videoRef.current) {
              videoRef.current.currentTime = videoRef.current.duration - 0.1;
              setVideoEnded(true);
              setIsPlaying(false);
            }
          }}
          onCanPlay={() => {
            if (!startAtEnd && videoRef.current && !videoReady) {
              setVideoReady(true);
              videoRef.current.play().then(() => {
                setIsPlaying(true);
              }).catch((error) => {
                console.error('Video oynatılamadı:', error);
              });
            }
          }}
          onError={(e) => {
            console.error('Video yükleme hatası:', e);
          }}
          onEnded={handleVideoEnd}
          onClick={videoEnded ? undefined : handleVideoClick}
          muted
          autoPlay={!startAtEnd}
          preload="auto"
          style={{
            width: '100vw',
            height: '100vh',
            objectFit: 'cover',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1,
            pointerEvents: videoEnded ? 'none' : 'auto'
          }}
        >
          <source src={`${STATIC_BASE_URL}/static/videos/Filmim.mp4`} type="video/mp4" />
          Tarayıcınız video oynatmayı desteklemiyor.
        </video>

        {/* Üniversite başlığı */}
        <div className={`university-title ${videoEnded ? 'video-ended' : ''}`}>
          <h1>KOCAELİ SAĞLIK VE TEKNOLOJİ ÜNİVERSİTESİ</h1>
          <p>Dijital Kampüs Haritası ve Bina Rehberi</p>
        </div>

        {/* Loading spinner */}
        {!isPlaying && !videoEnded && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Video yükleniyor...</p>
          </div>
        )}

        {/* Video bittikten sonra tıklanabilir noktalar */}
        {videoEnded && (() => {
          
          const coordsToUse = coordinates.length > 0 ? coordinates : testCoordinates;
          
          // Debug bilgisi
          console.log('=== DEBUG BİLGİSİ ===');
          console.log('videoEnded:', videoEnded);
          console.log('coordinates.length:', coordinates.length);
          console.log('testCoordinates.length:', testCoordinates.length);
          console.log('coordsToUse:', coordsToUse);
          console.log('coordsToUse.length:', coordsToUse.length);
          
          return (
            <div className="video-map-points" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'auto'
            }}>
              {coordsToUse.map((coord, index) => {
                console.log(`İğne ${index} render ediliyor:`, coord);
                const { left, top } = toClientPosition(coord.x, coord.y);
                return (
                  <div
                    key={index}
                    className="video-map-point"
                    style={{
                      left: `${left}px`,
                      top: `${top}px`,
                      position: 'absolute',
                      zIndex: 1000
                    }}
                    title={coord.building_name}
                    onClick={() => {
                      // Önce bina detaylarını yükle, sonra sayfa geçişi yap
                      onBuildingClick(coord.building_name);
                      // onVideoEnd() çağırmıyoruz çünkü handleBuildingClick zaten sayfa geçişini yapacak
                    }}
                  >
                    <div 
                      className={`video-point-marker ${coord.y < 300 ? 'popup-bottom' : ''}`}
                      data-title={coord.building_name}
                      style={{
                        width: '40px',
                        height: '40px',
                        cursor: 'pointer',
                        '--pin-color': colorPalette[index % colorPalette.length],
                        position: 'absolute',
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {/* Basit iğne şekli */}
                        <path 
                          fill={colorPalette[index % colorPalette.length]} 
                          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                          stroke="#fff"
                          strokeWidth="1"
                        />
                        <circle cx="12" cy="9" r="2" fill="white"/>
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Arama Ekranı - Video bittikten sonra */}
        {videoEnded && onSearchInputChange && (
          <div className="search-container">
            <div className="search-header">
              <div className="search-title">
                <h3>Oda Arama</h3>
              </div>
            </div>
            <div className="search-box">
              <input
                type="text"
                placeholder="Oda ara..."
                value={searchQuery}
                onChange={onSearchInputChange}
                className="search-input"
              />
            </div>
            
            {showSearchResults && searchResults.length > 0 && onSearchResultClick && (
              <div className="search-results">
                {searchResults.map((building, buildingIndex) => (
                  <div key={buildingIndex} className="search-building">
                    <h4>{building.building_name}</h4>
                    {building.matching_rooms.map((floor, floorIndex) => (
                      <div key={floorIndex} className="search-floor">
                        <h5>{floor.floor_name}</h5>
                        {floor.rooms.map((room, roomIndex) => (
                          <div
                            key={roomIndex}
                            className="search-room"
                            onClick={() => onSearchResultClick(building.building_name, {
                              building: building.building_name,
                              floor: floor.floor_name,
                              room: room
                            })}
                          >
                            <span className="room-name">{room.room_name}</span>
                            <span className="room-number">({room.room_number})</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            
            {/* Seçilen Arama Sonucu Detayları */}
            {selectedSearchResult && selectedSearchResult.roomData && (
              <div className="selected-search-result">
                <div className="result-header">
                  <h4>Seçilen Oda Bilgileri</h4>
                  <button 
                    className="close-result"
                    onClick={() => onSearchResultClick && onSearchResultClick('', null)}
                  >
                    ✕
                  </button>
                </div>
                <div className="result-content">
                  <div className="result-building">
                    <strong>Bina:</strong> {selectedSearchResult.buildingName}
                  </div>
                  <div className="result-floor">
                    <strong>Kat:</strong> {selectedSearchResult.roomData?.floor || 'Bilinmiyor'}
                  </div>
                  <div className="result-room">
                    <strong>Oda:</strong> {selectedSearchResult.roomData?.room?.room_name || 'Bilinmiyor'}
                  </div>
                  <div className="result-details">
                    <div><strong>Oda Numarası:</strong> {selectedSearchResult.roomData?.room?.room_number || 'Bilinmiyor'}</div>
                    <div><strong>Tür:</strong> {selectedSearchResult.roomData?.room?.room_type || 'Bilinmiyor'}</div>
                    <div><strong>Alan:</strong> {selectedSearchResult.roomData?.room?.area_sqm || 0} m²</div>
                    <div><strong>Kapasite:</strong> {selectedSearchResult.roomData?.room?.capacity || 0} kişi</div>
                  </div>
                  {/* Derslik Görseli - Direkt Göster */}
                  <div className="room-image-container">
                    <h4>Derslik Görseli</h4>
                    {console.log('🔍 PC DEBUG - Room name for image:', selectedSearchResult.roomData?.room?.room_name)}
                    {console.log('🔍 PC DEBUG - Generated URL:', getRoomImageUrl(selectedSearchResult.roomData?.room?.room_name))}
                    <img 
                      src={getRoomImageUrl(selectedSearchResult.roomData?.room?.room_name)}
                      alt={selectedSearchResult.roomData?.room?.room_name}
                      className="room-image"
                      onClick={() => handleImageClick(selectedSearchResult.roomData?.room?.room_name)}
                      onError={(e) => {
                        console.log('❌ PC ARAMA Derslik görseli yüklenemedi (.jpg):', e.target.src);
                        // .jpg yüklenemezse .png dene, sonra eski görseli dene
                        const baseFileName = selectedSearchResult.roomData?.room?.room_name
                          .toLowerCase()
                          .replace(/&/g, 've')
                          .replace(/[^a-z0-9çğıöşü]/g, '_')
                          .replace(/\//g, '_')
                          .replace(/_+/g, '_')
                          .replace(/^_|_$/g, '');
                        
                        // Önce optimize edilmiş .png dene
                        const newPngUrl = `${STATIC_BASE_URL}/static/room_images_optimized/${baseFileName}.png`;
                        console.log('🔄 PC ARAMA YENİ DERSLİK GÖRSELİ DENENİYOR (.png):', newPngUrl);
                        
                        // .png yüklenemezse eski görseli dene
                        const oldImage = new Image();
                        oldImage.onload = () => {
                          console.log('✅ PC ARAMA ESKİ DERSLİK GÖRSELİ YÜKLENDİ:', oldImage.src);
                          e.target.src = oldImage.src;
                        };
                        oldImage.onerror = () => {
                          console.log('❌ PC ARAMA ESKİ DERSLİK GÖRSELİ DE YÜKLENEMEDİ');
                        };
                        oldImage.src = newPngUrl;
                      }}
                      onLoad={(e) => {
                        console.log('✅ PC ARAMA Derslik görseli yüklendi:', e.target.src);
                      }}
                    />
                    <p className="image-click-hint">Görsele tıklayarak büyütebilirsiniz</p>
                  </div>

                  {onViewBuildingDetails && (
                    <div className="result-actions">
                      <button 
                        className="view-building-details-btn"
                        onClick={() => onViewBuildingDetails(selectedSearchResult.buildingName, selectedSearchResult.roomData?.floor)}
                      >
                        🏢 Bina Detaylarını Görüntüle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bilgilendirme Köşesi */}
        {videoEnded && (
          <div className="video-legend">
            <h3>📍 Giriş Kapıları</h3>
            <div className="legend-items">
              {coordinates.map((coord, index) => (
                <div key={index} className="legend-item">
                  <div className="legend-marker">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path 
                        fill={colorPalette[index % colorPalette.length]} 
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                        stroke="#fff"
                        strokeWidth="1"
                      />
                      <circle cx="12" cy="9" r="2" fill="white"/>
                    </svg>
                  </div>
                  <span className="legend-text">
                    {coord.building_name === 'FANTOM VE PRAKİLİNİK LABORATUVARI GİRİŞ KAPISI' 
                      ? 'Fantom ve Prakilinik Laboratuvarı ve Konferans Salonu' 
                      : coord.building_name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sayfa Altı Bilgilendirme */}
        {videoEnded && (
          <div className="page-info">
            <p>Haritadaki işaretli noktalara tıklayarak bina detaylarına ulaşabilirsiniz</p>
          </div>
        )}

        {/* Image Modal */}
        {imageModal.isOpen && (
          <div className="image-modal-overlay" onClick={closeImageModal}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="image-modal-header">
                <h3 className="image-modal-title">{imageModal.title}</h3>
                <button className="image-modal-close" onClick={closeImageModal}>×</button>
              </div>
              <img 
                src={imageModal.imageUrl} 
                alt={imageModal.title}
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  display: 'block',
                  transition: 'opacity 0.3s ease',
                  opacity: '0.7'
                }}
                onError={(e) => {
                  console.log('❌ PC ARAMA MODAL Derslik görseli yüklenemedi (.jpg):', e.target.src);
                  // .jpg yüklenemezse .png dene, sonra eski görseli dene
                  const baseFileName = imageModal.title
                    .toLowerCase()
                    .replace(/&/g, 've')
                    .replace(/[^a-z0-9çğıöşü]/g, '_')
                    .replace(/\//g, '_')
                    .replace(/_+/g, '_')
                    .replace(/^_|_$/g, '');
                  
                  // Önce optimize edilmiş .png dene
                  const newPngUrl = `${STATIC_BASE_URL}/static/room_images_optimized/${baseFileName}.png`;
                  console.log('🔄 PC ARAMA MODAL YENİ DERSLİK GÖRSELİ DENENİYOR (.png):', newPngUrl);
                  
                  // .png yüklenemezse eski görseli dene
                  const oldImage = new Image();
                  oldImage.onload = () => {
                    console.log('✅ PC ARAMA MODAL ESKİ DERSLİK GÖRSELİ YÜKLENDİ:', oldImage.src);
                    e.target.src = oldImage.src;
                  };
                  oldImage.onerror = () => {
                    console.log('❌ PC ARAMA MODAL ESKİ DERSLİK GÖRSELİ DE YÜKLENEMEDİ');
                  };
                  oldImage.src = newPngUrl;
                }}
                onLoad={(e) => {
                  console.log('✅ PC ARAMA MODAL Derslik görseli yüklendi:', e.target.src);
                  e.target.style.opacity = '1';
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoIntro;
